import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { useUser } from '@/hooks/useUser';
import { useUserOrganizationRole } from '@/hooks/useUserRole';
import { roleMapping } from '@/lib/roleMapping';
import { Building2, Key, LogOut } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChangePasswordDialog } from './ChangePasswordDialog';

function getInitials(firstName?: string, lastName?: string) {
  const f = firstName?.charAt(0)?.toUpperCase() || '';
  const l = lastName?.charAt(0)?.toUpperCase() || '';
  return f + l || '?';
}

export function User() {
  const { data: user, isLoading } = useUser();
  const { logout } = useAuth();
  const { tenantSlug } = useTenantSlug();
  const userOrganizationRole = useUserOrganizationRole();
  const navigate = useNavigate();
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
    useState(false);

  const isViewer = userOrganizationRole === 'VIEWER';

  if (isLoading) {
    return <div className="h-9 w-9 animate-pulse rounded-full bg-emerald-100" />;
  }

  if (!user) {
    return null;
  }

  const initials = getInitials(user.firstName, user.lastName);
  const roleName = userOrganizationRole
    ? roleMapping[userOrganizationRole]
    : null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold hover:bg-emerald-200 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2">
            {initials}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-64" align="end" forceMount>
          {/* User header */}
          <div className="flex items-center gap-3 p-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
              {roleName && (
                <Badge
                  variant="outline"
                  className="mt-1 text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"
                >
                  {roleName}
                </Badge>
              )}
            </div>
          </div>
          <DropdownMenuSeparator />
          {!isViewer && (
            <DropdownMenuItem
              onClick={() => navigate(`/o/${tenantSlug}/manage`)}
              className="gap-2"
            >
              <Building2 className="h-4 w-4 text-slate-400" />
              Mon organisation
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => setIsChangePasswordDialogOpen(true)}
            className="gap-2"
          >
            <Key className="h-4 w-4 text-slate-400" />
            Changer le mot de passe
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => logout()}
            className="gap-2 text-red-600 focus:text-red-600"
          >
            <LogOut className="h-4 w-4" />
            Se déconnecter
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordDialog
        open={isChangePasswordDialogOpen}
        onOpenChange={setIsChangePasswordDialogOpen}
      />
    </>
  );
}
